import { sessionSchema, setupInputSchema, setupStatusSchema } from '@canopee/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type SyntheticEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { apiRequest } from '../../api/client.js';
import { sessionQueryKey } from '../../auth/session.js';
import { formText } from '../../components/form-data.js';
import styles from '../login/login-screen.module.css';

function SetupFormFields({ locked }: { readonly locked: boolean }) {
  return (
    <>
      <label className={styles.field}>
        <span>Clé d’initialisation</span>
        <input autoComplete="off" disabled={locked} name="setupToken" required type="password" />
      </label>
      <label className={styles.field}>
        <span>Nom affiché</span>
        <input autoComplete="name" disabled={locked} name="displayName" required />
      </label>
      <label className={styles.field}>
        <span>Email</span>
        <input autoComplete="email" disabled={locked} name="email" required type="email" />
      </label>
      <label className={styles.field}>
        <span>Mot de passe</span>
        <input
          autoComplete="new-password"
          disabled={locked}
          name="password"
          required
          type="password"
        />
      </label>
      <label className={styles.field}>
        <span>Confirmer le mot de passe</span>
        <input
          autoComplete="new-password"
          disabled={locked}
          name="confirmation"
          required
          type="password"
        />
      </label>
    </>
  );
}

export function SetupScreen() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [formError, setFormError] = useState<string | null>(null);
  const status = useQuery({
    queryKey: ['setup-status'],
    queryFn: ({ signal }) => apiRequest('/api/v1/auth/setup/status', setupStatusSchema, { signal }),
    retry: false,
  });
  const setup = useMutation({
    mutationFn: ({ token, input }: { readonly token: string; readonly input: unknown }) =>
      apiRequest('/api/v1/auth/setup', sessionSchema, {
        method: 'POST',
        body: input,
        headers: { 'x-setup-token': token },
      }),
    onSuccess: async (session) => {
      queryClient.setQueryData(sessionQueryKey, session);
      await navigate('/', { replace: true });
    },
  });

  function submit(event: SyntheticEvent<HTMLFormElement, SubmitEvent>) {
    event.preventDefault();
    setFormError(null);
    const data = new FormData(event.currentTarget);
    const password = formText(data, 'password');
    if (password !== formText(data, 'confirmation')) {
      setFormError('Les deux mots de passe ne correspondent pas.');
      return;
    }
    const input = {
      email: data.get('email'),
      displayName: data.get('displayName'),
      password,
    };
    const parsed = setupInputSchema.safeParse(input);
    if (!parsed.success) {
      setFormError(parsed.error.issues[0]?.message ?? 'Vérifiez les informations saisies.');
      return;
    }
    setup.mutate({ token: formText(data, 'setupToken'), input: parsed.data });
  }

  const locked = status.isSuccess && !status.data.available;
  return (
    <main className={styles.page}>
      <form className={styles.card} onSubmit={submit}>
        <div>
          <p className={styles.eyebrow}>Maintenance initiale</p>
          <h1>Premier administrateur</h1>
          <p>Ce parcours se ferme définitivement dès la création du premier compte.</p>
        </div>
        {locked ? (
          <p className={styles.success} role="status">
            L’initialisation est terminée. Aucun second administrateur ne peut être créé ici.
          </p>
        ) : null}
        <SetupFormFields locked={locked} />
        {setup.isError || status.isError || formError !== null ? (
          <p aria-live="polite" className={styles.error} role="alert">
            {setup.isError
              ? setup.error.message
              : status.isError
                ? status.error.message
                : formError}
          </p>
        ) : null}
        <button
          className={styles.submit}
          disabled={locked || setup.isPending || status.isPending}
          type="submit"
        >
          {setup.isPending ? 'Initialisation…' : 'Créer le premier administrateur'}
        </button>
        <Link className={styles.link} to="/login">
          Retour à la connexion
        </Link>
      </form>
    </main>
  );
}
